import { useEffect, useState } from 'react';
import { Radio, RadioGroup } from '@headlessui/react';
import Footer from '../header_footer/Footer';
import Navigation from '../header_footer/Navgation';
import Breadcrumb from './components/Breadcrumb';
import { blackTextLogo } from '../../assets/images/logos';
import RatingStars from '../../utils/RatingStars';
import { useRefresh } from '../../utils/RefreshProvider';
import axios from 'axios';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import ReviewsPart from './ReviewsPart';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import Notify from '../../utils/useNotify';
import ColorSelector from './components/ColorSelector';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { fileNotFound } from '../../assets/images/icons';
import RelatedProducts from './RelatedProducts';

export default function Product() {
  const { t } = useTranslation();
  const { triggerRefresh, refreshKey } = useRefresh();
  const token = localStorage.getItem('token');
  const sessionID = localStorage.getItem('session_id');
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;
  const { slug } = useParams();

  const [product, setProduct] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [reviewsCount, setReviewsCount] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);

  useEffect(() => {
    const fetchSize = async () => {
      try {
        const res = await axios.get(`${localhost}all-sizes`);
        setSizes(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchData = async () => {
      try {
        const res = await axios.get(`${localhost}get-product/${slug}`);
        const productData = res.data.product;
        const parsedImgs = productData.imgs ? JSON.parse(productData.imgs) : null;
        const parsedCategory = JSON.parse(productData.category);
        const updatedProduct = { ...productData, imgs: parsedImgs, category: parsedCategory };
        setProduct(updatedProduct);
        setColors(updatedProduct.colors);

        if (updatedProduct.colors.length > 0) {
          setSelectedColorId(updatedProduct.colors[0].color_id);
        }
        setMainImage(updatedProduct.imgs ? localimg + updatedProduct.imgs[0] : fileNotFound);
        setBreadcrumbItems([
          { label: updatedProduct.gender, href: `/shop?gender=${updatedProduct.gender}` },
          { label: updatedProduct.category[i18next.language], href: `/shop?gender=${updatedProduct.gender}&category=${updatedProduct.category_id}` },
          { label: updatedProduct.name[i18next.language], href: `/product/${updatedProduct.slug}` }
        ]);

        setReviewsCount(res.data.reviews);
        const stockResponse = await axios.get(`${localhost}product-stock/${updatedProduct.id}`);
        setStockData(stockResponse.data);

        if (updatedProduct.colors.length === 0) {
          const availableSizeIds = stockResponse.data
            .filter(stock => stock.stock > 0 && !stock.color_id)
            .map(stock => stock.size_id);
          setAvailableSizes(availableSizeIds);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSize();
    fetchData();
  }, [refreshKey]);

  useEffect(() => {
    if (selectedColorId) {
      const availableSizeIds = stockData
        .filter(stock => stock.color_id === selectedColorId && stock.stock > 0)
        .map(stock => stock.size_id);
      setAvailableSizes(availableSizeIds);

      if (selectedSize && !availableSizeIds.includes(selectedSize)) {
        setSelectedSize(null);
      }
    }
  }, [selectedColorId, stockData]);

  const isSizeAvailable = (sizeId) => {
    if (colors.length === 0) {
      return availableSizes.includes(sizeId);
    }
    return selectedColorId ? availableSizes.includes(sizeId) : false;
  };

  const isColorAvailable = (colorId) => {
    return stockData.some(stock => stock.color_id === colorId && stock.stock > 0);
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (selectedSize === null) {
      Notify("warning", t("size-warning"));
      return;
    }
    if (colors.length > 0) {
      if (selectedColorId === null) {
        Notify("warning", t("color-warning"));
        return;
      }
    }

    let currentSessionID = sessionID;
    if (currentSessionID === null) {
      try {
        const SessionData = await axios.get(localhost + "create-session");
        currentSessionID = SessionData.data.session_id;
        localStorage.setItem('session_id', currentSessionID);
      } catch (err) {
        console.error(err);
        return;
      }
    }

    const stockItem = stockData.find(stock =>
      stock.size_id === selectedSize &&
      (colors.length === 0 || stock.color_id === selectedColorId)
    );

    if (!stockItem || stockItem.stock < quantity) {
      Notify("error", t("insufficient-stock"));
      return;
    }

    setBtnLoading(true);
    const data = {
      product_id: product.id,
      size_id: selectedSize,
      color_id: selectedColorId,
      quantity: quantity,
      session_id: currentSessionID,
    };

    try {
      await axios.post(localhost + "user/orders", data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      Notify("success", t("add-bag-success"));
      triggerRefresh();
    } catch (err) {
      console.error(err);
      localStorage.removeItem("session_id");
      Notify("error", t("add-bag-error"));
    } finally {
      setBtnLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <section>
      <Navigation />
      <main className="container mx-auto pt-10 sm:pt-16 p-4">
        <Breadcrumb items={breadcrumbItems} />
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-8 lg:gap-0 mt-6">
          <div className="lg:col-span-4 order-1 lg:pr-10">
            <div className="flex flex-col-reverse md:flex-row gap-4">
              <div className="flex md:flex-col gap-2 overflow-x-auto md:w-16">
                {product.imgs?.map((img, index) => (
                  <img loading="lazy" key={index} src={localimg + img} alt={`Product thumbnail ${index + 1}`} onClick={() => setMainImage(localimg + img)}
                    className={`w-16 aspect-[64/85] object-cover object-top border border-gray-200 cursor-pointer ${mainImage === localimg + img ? "border-b-2 border-b-black" : "border-b-2 border-transparent"}`}
                  />
                ))}
                {!product.imgs && (
                  <img loading="lazy" src={fileNotFound} alt="Product thumbnail" onClick={() => setMainImage(fileNotFound)}
                    className={`w-16 aspect-[64/85] object-cover object-top border border-gray-200 cursor-pointer ${mainImage === fileNotFound ? "border-b-2 border-b-black" : "border-b-2 border-transparent"}`}
                  />
                )}
              </div>
              <div className="flex-1">
                <img loading="lazy" src={mainImage} alt="Main product" className="w-full aspect-[750/800] border border-gray-200 object-cover object-top rounded-lg" />
              </div>
            </div>
          </div>

          <div className="order-2 lg:col-span-3 lg:pl-10 lg:border-l">
            <div className="sticky top-8">
              <img loading="lazy" src={blackTextLogo} alt='black-logo' className='w-28 h-auto mb-3' />
              <h1 className="text-2xl font-bold tracking-tight text-black sm:text-3xl mb-3">{product.name[i18next.language]}</h1>
              <p className="text-3xl tracking-tight text-black">{product.price}<span className='pl-0.5 text-sm font-semibold'>MAD</span></p>

              <div className="mt-6">
                <div className="flex items-center">
                  <RatingStars rating={product.ratings} size='18px' color='black' />
                  <p className="ml-3 text-sm font-medium text-orange-600 hover:text-orange-500">
                    {reviewsCount} {t("reviews")}
                  </p>
                </div>
              </div>

              <form onSubmit={handleOrder}>
                <fieldset aria-label="Choose a color" className='mt-6'>
                  <legend className="text-sm font-medium text-black">{t("quantity")}</legend>
                  <div className="mt-4 flex items-center gap-2">
                    <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                      <span className="sr-only">Decrease quantity</span>
                      <MinusIcon className='size-4' />
                    </button>
                    <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(99, Number(e.target.value))))} min="1" max="99"
                      className="h-8 w-16 rounded-md border border-gray-300 text-center text-black focus:outline-none focus:ring-2 focus:ring-orange-500" />
                    <button type="button" onClick={() => setQuantity(Math.min(99, quantity + 1))} className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                      <span className="sr-only">Increase quantity</span>
                      <PlusIcon className='size-4' />
                    </button>
                  </div>
                </fieldset>

                <div className="mt-8">
                  {colors.length > 0 && (
                    <div className='mt-8'>
                      <ColorSelector
                        colors={colors.map(color => ({
                          ...color,
                          disabled: !isColorAvailable(color.color_id)
                        }))}
                        t={t}
                        selectedColorId={selectedColorId}
                        setSelectedColorId={setSelectedColorId}
                      />
                    </div>
                  )}
                  <div className="mt-8">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-black">Size</h3>
                      <div />
                    </div>
                    <fieldset aria-label="Choose a size" className="mt-4">
                      <RadioGroup value={selectedSize} onChange={setSelectedSize} className="grid grid-cols-4 gap-3">
                        {sizes.map((size) => {
                          const isAvailable = isSizeAvailable(size.id);

                          return (
                            <Radio key={size.id} value={size.id} disabled={!isAvailable}
                              className={`group relative flex items-center justify-center rounded-md border px-4 py-4 md:py-5 text-sm font-semibold uppercase hover:bg-gray-50 focus:outline-none data-[checked]:ring-2 data-[checked]:ring-black 
                              ${isAvailable ? 'cursor-pointer bg-white text-black shadow-sm' : 'cursor-not-allowed bg-gray-50 text-gray-200'}`}>
                              {size.name}
                              {!isAvailable && (
                                <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-md border border-gray-200">
                                  <svg stroke="currentColor" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full stroke-2 text-gray-200">
                                    <line x1={0} y1={100} x2={100} y2={0} vectorEffect="non-scaling-stroke" />
                                  </svg>
                                </span>
                              )}
                            </Radio>
                          );
                        })}
                      </RadioGroup>
                    </fieldset>
                  </div>

                  <button type='submit' className={`mt-8 w-full rounded-md px-8 py-3 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-black/5 focus:ring-offset-2
                    ${btnLoading ? "bg-black/60 cursor-not-allowed" : "bg-black hover:bg-black/80"}`} disabled={btnLoading}>
                    {btnLoading ? (
                      <span className="flex justify-center items-center">
                        <ArrowPathIcon className="animate-spin text-white size-6 mr-2" />
                        {t("loading")}
                      </span>
                    ) : (
                      t("add-bag")
                    )}
                  </button>
                </div>
              </form>

              <div className="border-t mt-8 pt-8">
                <div>
                  <h3 className="text-sm font-semibold text-black">{t("description")}</h3>
                  <div className="space-y-6 mt-4">
                    <p className="text-base text-black">{product.description[i18next.language]}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ReviewsPart productId={product.id} ratingProp={product.ratings} />
        <RelatedProducts categoryId={product.category_id} localhost={localhost} t={t} />
      </main>
      <Footer />
    </section>
  );
}